import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { ContentUpgrades } from "@swizec/gatsby-theme-course-platform";
export const _frontmatter = {
  "title": "The Magic Function principle",
  "description": "Write functions, mostly magic.",
  "published": "2021-02-05T00:00:00.000Z",
  "categories": "Technical, Architecture, Mindset",
  "content_upgrade": "SeniorMindset",
  "hero": "./img/screenshot-1612545897378.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p><em parentName="p">{`"Hey how does the foobulizer work again?"`}</em></p>
    <p>{`Ugh not this again. You've been answering this same damn question for months. How are they still not getting it?? 🙄`}</p>
    <p>{`It's simple!`}</p>
    <p>{`You take the gnasher, connect it to the third function call from the left on a full moon, or the fourth one from top, if it's Sunday, then you plug into the fantalizer, add 3 lines of code using the in-house configuration language built from JSON, stand on your right leg, hold the left nostril, and voila: the foobulizer.`}</p>
    <p>{`I mean seriously, are they all stupid?`}</p>
    <h2 {...{
      "id": "write-the-docs"
    }}>{`Write the docs`}</h2>
    <p>{`Your team is smart.`}</p>
    <p>{`When you get (or ask) the same question over and over, that's a symptom. The problem is the codebase. Or the architecture. Or the data model. Or the docs.`}</p>
    <p>{`Your team can't help itself. They need you.`}</p>
    <p>{`Fantastic job security aaaaand that's `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/why-senior-engineers-get-nothing-done"
      }}>{`why senior engineers get nothing done`}</a>{`. There's no time. 💩`}</p>
    <p>{`When you answer the same question 3 times, write it down. Share the link.`}</p>
    <p>{`When they ask again, edit and share the link.`}</p>
    <p>{`Your doc won't always answer all the nuance of a question but it helps. Gets you on common ground, makes the chat more productive.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`If your team keeps asking a question, write a doc.`}<br parentName="p"></br><br parentName="p"></br>{`If they keep asking, build the magic function that does the thing. Make it [easily] discoverable in your codebase.`}<br parentName="p"></br><br parentName="p"></br>{`If you don't, they *will* find the wrong way 😉`}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/seniormindset?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#seniormindset`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1357443311758286848"
      }}>{`February 4, 2021`}</a></blockquote>
    <p>{`And the more you write, the less you have to keep in your head. Mine has the memory of a goldfish 😇`}</p>
    <ContentUpgrades.SeniorMindset />
    <h2 {...{
      "id": "the-magic-function"
    }}>{`The Magic Function`}</h2>
    <p>{`You know what's even better than up-to-date docs? Not needing docs.`}</p>
    <p>{`Imagine if instead of a complex 6 step process you could start the foobulizer with `}<inlineCode parentName="p">{`startFoobulizer()`}</inlineCode>{`.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/3oKIPeVAg5Imgf3m5q/giphy-loop.mp4?cid=7cf28b0f49hnv4h7o4puvxk0g5kp3h12sh1bcs9lpje6l69m&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`We used to have this problem at work. We still do but not with that part 😛`}</p>
    <h3 {...{
      "id": "the-magic-function-saved-my-butt"
    }}>{`The Magic Function saved my butt`}</h3>
    <p>{`Verifying insurance was my first introduction to `}<em parentName="p">{`"Holy shit health tech is something else"`}</em>{`.`}</p>
    <p>{`The process goes like this: Get your info, maybe use what exists, talk to an API, see if it's valid, check a sea of inscrutable codes, decide if your insurance supports `}<em parentName="p">{`this`}</em>{` appointment at `}<em parentName="p">{`this`}</em>{` time (yes it changes), and a bunch other details.`}</p>
    <p>{`You don't want to deal with this as a user.`}</p>
    <p>{`Heck, I didn't wanna deal with it as a programmer! Confusing, terrible, makes-no-sense.`}</p>
    <p>{`We had an `}<inlineCode parentName="p">{`Insurance`}</inlineCode>{` module, but it was hard to hold. You had to call the right functions in the right sequence at the right time.`}</p>
    <p>{`Get it wrong and the company doesn't get paid.`}</p>
    <p>{`I got it wrong. Five times 😅`}</p>
    <p>{`Edge case after edge case, bug after bug, meeting after meeting. 3 of us working on this stupid thing.`}</p>
    <p>{`We had docs, we had code, yet we couldn't make it work.`}</p>
    <p>{`Until one day the dude who grokked the business logic said `}<em parentName="p">{`"Screw this, this is bullshit."`}</em></p>
    <p>{`He built a new function: `}<inlineCode parentName="p">{`Insurance.verify(user, appointment)`}</inlineCode>{`.`}</p>
    <p>{`And oh my god it was beautiful. Cut through all the crap. You call this method, give it a user and an appointment, `}<em parentName="p">{`and it does it all for you`}</em>{`. Standing on the right leg holding its left nostril and all! 😍`}</p>
    <p>{`Nobody has asked an insurance question since.`}</p>
    <h3 {...{
      "id": "why-the-magic-function-works"
    }}>{`Why The Magic Function works`}</h3>
    <p>{`The Magic Function is a high level method that `}<em parentName="p">{`does the thing the whole thing and nothing but the thing`}</em>{`.`}</p>
    <p>{`Forget reading and understanding docs. Forget writing and updating docs. Just make it work.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Why is it called The Magic Function principle?`}<br parentName="p"></br><br parentName="p"></br>{`Because of this quote from Penn & Teller. *you* do the work so the rest of your team doesn't have to `}<a parentName="p" {...{
          "href": "https://t.co/eM7B32L7p4"
        }}>{`pic.twitter.com/eM7B32L7p4`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1357454099889881089"
      }}>{`February 4, 2021`}</a></blockquote>
    <p><em parentName="p">{`You`}</em>{` do the work so your team doesn't have to. Like Penn & Teller say:`}</p>
    <blockquote>
      <p parentName="blockquote">{`Sometimes magic is just someone spending more time on something than anyone else might reasonably expect.
~ Teller of Penn&Teller fame`}</p>
    </blockquote>
    <p>{`You write The Magic Function when you see that code `}<em parentName="p">{`always`}</em>{` goes together. You `}<em parentName="p">{`always`}</em>{` make this DB update after that API call. You `}<em parentName="p">{`always`}</em>{` call these 3 functions together. You `}<em parentName="p">{`always`}</em>{` ...`}</p>
    <p>{`The Magic Function frees your team from that complexity. Instead of worrying about the details, they focus on `}<em parentName="p">{`their`}</em>{` problem. 🧙`}</p>
    <p>{`Best part: `}<strong parentName="p">{`they'll find your function on their own.`}</strong>{` No questions, no confusion.`}</p>
    <p>{`Engineers always find a way. Help them find `}<em parentName="p">{`the`}</em>{` way.`}</p>
    <h2 {...{
      "id": "ye-olde-unix-philosophy"
    }}>{`Ye olde unix philosophy??`}</h2>
    <p>{`The opposite of The Magic Function is the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Unix_philosophy"
      }}>{`unix philosophy`}</a>{` – lots of small composable utilities.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Make each program do one thing well. To do a new job, build afresh rather than complicate old programs by adding new "features". Expect the output of every program to become the input to another, as yet unknown, program`}</p>
    </blockquote>
    <p>{`How you read this depends on your idea of "one thing".`}</p>
    <p>{`Is one thing to verify insurance, or to call an API for 1 out of 50 different ways it needs to be called depending on circumstance? 🤔`}</p>
    <p>{`It's fractal. Single responsibility all the way down. A magic function calls other magic functions.`}</p>
    <p>{`You wouldn't wanna think about optimizing DOM updates to draw a button, just like your HTML rendering shouldn't think about where the HTML is from.`}</p>
    <p>{`Write functions, mostly magic. Think fractals.`}</p>
    <p>{`✌️`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: `}<a parentName="p" {...{
        "href": "https://youtu.be/kr53h0hv8hI"
      }}>{`how to start a 1928 Ford`}</a>{` is a 20min video. These days you press a button and go. When's the last time you read the instruction manual for a car?`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      